import { Alert, AlertTitle } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query"
import axios from "axios";
import moment from "moment";
import { useState } from "react";
import { ReactQueryKeys } from "src/constants/ReactQueryKeys"
import { isMobile } from "src/helpers/windowHelpers";
import { useAppDispatch, useAppSelector } from "src/hooks";
import { clearOldIncidents, increaseIncident } from "src/redux/slices/statuspage.slice";

export default function StatusPage() {
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [maintenances, setMaintenances] = useState<ScheduledMaintenance[]>([]);

    const dispatch = useAppDispatch();
    const closedIncidents = useAppSelector(state => state.statuspage.closedIncidents)

    useSuspenseQuery({
        queryKey: [ReactQueryKeys.STATUSPAGE, "incidents"],
        queryFn: async () => {
            try {
                if (!import.meta.env.VITE_STATUSPAGE_COMPONENT_ID || !import.meta.env.VITE_STATUSPAGE_PAGE_ID)
                    return [];

                const response = await axios.get<StatusPage>(`https://${import.meta.env.VITE_STATUSPAGE_PAGE_ID}.statuspage.io/api/v2/incidents/unresolved.json`);
                const responseIncidents = response.data.incidents || [];
                setIncidents(responseIncidents.filter((item: Incident) => item.components.some((comp: Component) => comp.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID)));
                dispatch(clearOldIncidents());
                return [];
            }
            catch (err) {
                // ignored
                return [];
            }
        },
        refetchInterval: 3000,
        refetchIntervalInBackground: false
    });

    useSuspenseQuery({
        queryKey: [ReactQueryKeys.STATUSPAGE, "maintenances"],
        queryFn: async () => {
            try {
                if (!import.meta.env.VITE_STATUSPAGE_COMPONENT_ID || !import.meta.env.VITE_STATUSPAGE_PAGE_ID)
                    return [];
                
                const [upcoming, active] = await Promise.all([
                    axios.get<StatusPage>(`https://${import.meta.env.VITE_STATUSPAGE_PAGE_ID}.statuspage.io/api/v2/scheduled-maintenances/upcoming.json`),
                    axios.get<StatusPage>(`https://${import.meta.env.VITE_STATUSPAGE_PAGE_ID}.statuspage.io/api/v2/scheduled-maintenances/active.json`)
                ]);
                const upcomingData = upcoming.data.scheduled_maintenances || [];
                const activeData = active.data.scheduled_maintenances || [];
                const responseScheduledMaintenances = [...upcomingData, ...activeData];
                setMaintenances(responseScheduledMaintenances.filter((item: ScheduledMaintenance) => item.components.some((comp: Component) => comp.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID)));
                dispatch(clearOldIncidents());
                return [];
            }
            catch (err) {
                // ignored
                return [];
            }
        },
        refetchInterval: 3000,
        refetchIntervalInBackground: false
    });

    const maintance = maintenances
        .filter(item => !closedIncidents.find(incident => incident.key === `${item.id}|${item.status}`))
        .filter(item => {
            const component = item.components.find(item => item.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID);
            return !!component
        })
        .filter(item => moment().isSame(item.scheduled_for, 'day') && moment().isBefore(item.scheduled_until))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .at(0);

    const incident = incidents
        .filter(item => !closedIncidents.find(incident => incident.key === `${item.id}|${item.status}`))
        .filter(item => {
            const component = item.components.find(item => item.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID);
            return !!component && component.status !== 'operational'
        })
        .filter(item => item.status !== 'completed')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .at(0);

    function closeIncidents(theMaintenances: boolean) {
        if (theMaintenances) {
            maintenances
                .forEach(item => dispatch(increaseIncident({ id: item.id, status: item.status })))
        }
        else
            incidents
                .forEach(item => dispatch(increaseIncident({ id: item.id, status: item.status })))
    }

    const windowIsMobile = isMobile()
    return (
        <div style={{ position: 'fixed', bottom: 20, right: windowIsMobile ? 10 : 40, zIndex: 9999, display: 'flex', gap: 20, flexDirection: 'column', alignItems: 'end' }}>
            {maintance && <CustomAlert maintenance={maintance} closeIncidents={closeIncidents} />}
            {incident && <CustomAlert incident={incident} closeIncidents={closeIncidents} />}
        </div>
    )
}

const incidentMessages = [
    {
        incident_status: "investigating",
        title: "Estamos cientes de um problema em nossos serviços",
        message: "Detectamos um possível problema que pode estar afetando parte de nossos serviços. Nossa equipe está investigando a causa e vamos manter você informado com atualizações contínuas. Agradecemos a sua paciência."
    },
    {
        incident_status: "identified",
        title: "Incidente identificado – estamos trabalhando na solução",
        message: "Confirmamos um incidente que pode impactar o funcionamento de nossos serviços. Já estamos atuando para mitigar os efeitos e restaurar o serviço o mais rápido possível. Novas atualizações serão publicadas em breve."
    },
    {
        incident_status: "monitoring",
        title: "Estamos monitorando a estabilidade do sistema",
        message: "Aplicamos correções e os serviços estão se recuperando. Estamos monitorando cuidadosamente para garantir que tudo continue funcionando normalmente."
    },
    {
        incident_status: "resolved",
        title: "Incidente resolvido com sucesso",
        message: "O problema foi identificado e resolvido. Todos os serviços já estão operando normalmente. Agradecemos pela compreensão e continuamos à disposição para qualquer dúvida."
    }
];

const maintenanceMessages = [
    {
        incident_status: "scheduled",
        message: "Uma manutenção está agendada. O sistema poderá passar por instabilidades ou ficar temporariamente indisponível durante esse período."
    },
    {
        incident_status: "in_progress",
        message: "Uma manutenção está em andamento. Algumas funcionalidades podem estar indisponíveis ou apresentar lentidão durante o período informado."
    },
    {
        incident_status: "verifying",
        message: "Uma manutenção foi concluída e estamos realizando verificações finais para garantir que o sistema esteja funcionando normalmente."
    }
];

const impact: any = {
    "degraded_performance": "Performance degradada",
    "partial_outage": "Interrupção parcial",
    "major_outage": "Incidente Crítico ou Fora do Ar"
}

interface AlertProps {
    incident?: Incident,
    maintenance?: ScheduledMaintenance,
    closeIncidents: (theMaintenances: boolean) => void
}
function CustomAlert(props: AlertProps) {

    const severity = props.maintenance
        ? 'info'
        : 'warning';

    const component = props.maintenance
        ? props.maintenance?.components.find(item => item.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID)
        : props.incident?.components.find(item => item.id === import.meta.env.VITE_STATUSPAGE_COMPONENT_ID);

    if (!component)
        return null;

    const title = props.maintenance
        ? props.maintenance.status === 'in_progress'
            ? "Manutenção em andamento"
            : props.maintenance.status === 'verifying' ? 'Manutenção sob revisão'
                : "Manutenção programada"
        : incidentMessages.find(item => item.incident_status === props.incident?.status)?.title
    const description = props.maintenance
        ? maintenanceMessages.find(item => item.incident_status === props.maintenance?.status)?.message
        : incidentMessages.find(item => item.incident_status === props.incident?.status)?.message;


    return (
        <>
            <Alert
                severity={severity}
                variant="filled"
                style={{ maxWidth: '80vw', width: 350 }}
                onClose={() => props.closeIncidents(props.maintenance !== undefined)}
            >
                <AlertTitle style={{ fontSize: 16 }}>{title}</AlertTitle>
                {props.incident && impact[component.status] && (
                    <>
                        <span>{impact[component.status]}</span>
                        <br /><br />
                    </>
                )}
                <span style={{ fontSize: 14 }}>{description}</span>
                {props.maintenance && (
                    <>
                        <br /><br />
                        <span>{moment(props.maintenance.scheduled_for).format('DD/MM/YYYY HH:mm')} - {moment(props.maintenance.scheduled_until).format('DD/MM/YYYY HH:mm')}</span>
                    </>
                )}
                <br />
                <br />
                <a href="https://meupedagio.statuspage.io" target="_blank" style={{ color: props.maintenance ? '#FFF' : '#000', fontSize: 12 }}>Saiba mais</a>
            </Alert>
        </>
    )
}

export interface StatusPage {
    page: Page;
    incidents: Incident[];
    scheduled_maintenances?: ScheduledMaintenance[];
}

export interface Page {
    id: string;
    name: string;
    url: string;
    time_zone: string;
    updated_at: string;
}

export interface Incident {
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
    monitoring_at: string | null;
    resolved_at: string | null;
    impact: string;
    shortlink: string;
    started_at: string;
    page_id: string;
    incident_updates: IncidentUpdate[];
    components: Component[];
    reminder_intervals: null;
}

export interface ScheduledMaintenance {
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
    monitoring_at: string | null;
    resolved_at: string | null;
    impact: string;
    shortlink: string;
    started_at: string;
    page_id: string;
    incident_updates: IncidentUpdate[];
    components: Component[];
    scheduled_for: string;
    scheduled_until: string;
}

export interface IncidentUpdate {
    id: string;
    status: string;
    body: string;
    incident_id: string;
    created_at: string;
    updated_at: string;
    display_at: string;
    affected_components: AffectedComponent[];
    deliver_notifications: boolean;
    custom_tweet: string | null;
    tweet_id: string | null;
}

export interface AffectedComponent {
    code: string;
    name: string;
    old_status: string;
    new_status: string;
}

export interface Component {
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
    position: number;
    description: string | null;
    showcase: boolean;
    start_date: string;
    group_id: string | null;
    page_id: string;
    group: boolean;
    only_show_if_degraded: boolean;
}
